import { useProject } from '@/lib/project-context'
import { useAuthState } from '@/lib/auth-state'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDateLocale } from '@/i18n/dates'
import { formatCredits, isOrgAdminRole } from '@hub/contracts'
import { orpc } from '@/lib/orpc-client'
import { toApiUrl } from '@/lib/orpc-client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { GuestCheckbox } from '@/components/guest-checkbox'
import { OrgRoleInfoPopover } from '@/components/org-role-info-popover'
import { Coins, FolderOpen, Lock, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SettingsPage, SettingsRow, SettingsSection, settingsInputClass } from './settings-layout'

type OrgDetail = Awaited<ReturnType<typeof orpc.organizations.get>>['organization']
type OrgMember = Awaited<ReturnType<typeof orpc.organizations.listMembers>>['members'][number]

const AUTH_PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
}

export function OrganizationSettings() {
  const { project } = useProject()
  const { me } = useAuthState()
  const { t } = useTranslation('settings')
  const { intl } = useDateLocale()
  const organizationId = project.organizationId

  const orgMembership = me?.memberships.organizations.find(
    (m) => m.organizationId === organizationId
  )
  const isOwner = orgMembership?.role === 'owner'

  const [org, setOrg] = useState<OrgDetail | null>(null)
  const [members, setMembers] = useState<OrgMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Settings form state
  const [authProvider, setAuthProvider] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveNotice, setSaveNotice] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [orgRes, membersRes] = await Promise.all([
        orpc.organizations.get({ organizationId }),
        orpc.organizations.listMembers({ organizationId }),
      ])
      setOrg(orgRes.organization)
      setAuthProvider(orgRes.organization.requiredAuthProvider ?? '')
      setMembers(membersRes.members)
    } catch {
      setError(t('organization.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void load()
  }, [load])

  const handleSaveSettings = async () => {
    setSaveError(null)
    setSaveNotice(null)
    setIsSaving(true)
    try {
      await orpc.organizations.updateSettings({
        organizationId,
        requiredAuthProvider: (authProvider || null) as 'google' | 'microsoft' | null,
      })
      setSaveNotice(t('organization.saved'))
      void load()
    } catch {
      setSaveError(t('organization.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <SettingsPage>
        <p className="py-6 text-sm text-muted-foreground">{t('organization.loading')}</p>
      </SettingsPage>
    )
  }

  if (error || !org) {
    return (
      <SettingsPage>
        <p className="py-6 text-sm text-destructive">
          {error ?? t('organization.loadFailedGeneric')}
        </p>
      </SettingsPage>
    )
  }

  const stats = [
    {
      icon: FolderOpen,
      value: `${org.projectCount} / ${org.projectLimit}`,
      label: t('organization.projectsWithLimit', { limit: org.projectLimit }),
    },
    { icon: Users, value: String(org.memberCount), label: t('organization.totalMembers') },
    {
      icon: Lock,
      value: org.requiredAuthProvider
        ? AUTH_PROVIDER_LABELS[org.requiredAuthProvider]
        : t('organization.authAny'),
      label: t('organization.authMethod'),
    },
  ]

  return (
    <SettingsPage>
      {/* Overview: the org's name and its three numbers, flat. */}
      <SettingsSection title={org.name}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {stats.map((stat) => (
            <div key={stat.label} className="flex items-center gap-3">
              <div className="rounded-lg bg-muted p-2">
                <stat.icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-semibold text-foreground tabular-nums">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </SettingsSection>

      {/* Credits — read-only; only super-admins change the budget (D4). */}
      <SettingsSection
        title={t('organization.creditsTitle')}
        description={t('organization.creditsHint')}
      >
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-muted p-2">
            <Coins className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-lg font-semibold text-foreground tabular-nums">
              {formatCredits(org.creditsUsed, intl)} / {formatCredits(org.creditLimit, intl)}
            </p>
            <p className="text-xs text-muted-foreground">
              {org.creditsReset === 'monthly' && org.creditsPeriodEnd
                ? t('organization.creditsMonthly', {
                    date: new Date(org.creditsPeriodEnd).toLocaleDateString(intl, {
                      day: 'numeric',
                      month: 'short',
                    }),
                  })
                : t('organization.creditsManual')}
            </p>
          </div>
        </div>
      </SettingsSection>

      {/* Settings section — owner only */}
      {isOwner && (
        <SettingsSection title={t('organization.settingsTitle')}>
          <SettingsRow
            label={t('organization.authMethod')}
            description={t('organization.authHint')}
            htmlFor="org-auth-provider"
          >
            <select
              id="org-auth-provider"
              className={cn(settingsInputClass, 'w-48')}
              value={authProvider}
              onChange={(e) => setAuthProvider(e.target.value)}
            >
              <option value="">{t('organization.authAny')}</option>
              <option value="google">Google</option>
              <option value="microsoft">Microsoft</option>
            </select>
          </SettingsRow>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
          {saveNotice && <p className="text-sm text-muted-foreground">{saveNotice}</p>}

          <div>
            <Button onClick={() => void handleSaveSettings()} disabled={isSaving}>
              {isSaving ? t('organization.saving') : t('organization.save')}
            </Button>
          </div>
        </SettingsSection>
      )}

      {/* Members table */}
      <OrgMembersTable
        organizationId={organizationId}
        members={members}
        isOwner={isOwner}
        onMembersChanged={() => void load()}
      />
    </SettingsPage>
  )
}

function OrgMembersTable({
  organizationId,
  members,
  isOwner,
  onMembersChanged,
}: {
  organizationId: string
  members: OrgMember[]
  isOwner: boolean
  onMembersChanged: () => void
}) {
  const { t } = useTranslation('settings')
  const { intl } = useDateLocale()
  const roleLabel = (role: string) =>
    role === 'owner' || role === 'admin' || role === 'member' ? t(`role.${role}`) : role
  const [savingUserId, setSavingUserId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const ownerCount = members.filter((m) => m.role === 'owner').length

  const handleRoleChange = async (userId: string, role: string) => {
    setSaveError(null)
    setSavingUserId(userId)
    try {
      await orpc.organizations.updateMember({
        organizationId,
        userId,
        role: role as 'owner' | 'admin' | 'member',
      })
      onMembersChanged()
    } catch {
      setSaveError(t('organization.roleFailed'))
    } finally {
      setSavingUserId(null)
    }
  }

  const handleToggleDisabled = async (userId: string, disabled: boolean) => {
    setSaveError(null)
    setSavingUserId(userId)
    try {
      await orpc.organizations.toggleMemberDisabled({
        organizationId,
        userId,
        disabled,
      })
      onMembersChanged()
    } catch {
      setSaveError(t('organization.statusFailed'))
    } finally {
      setSavingUserId(null)
    }
  }

  const handleGuestChange = async (userId: string, isGuest: boolean) => {
    setSaveError(null)
    setSavingUserId(userId)
    try {
      await orpc.organizations.updateMember({
        organizationId,
        userId,
        isGuest,
      })
      onMembersChanged()
    } catch {
      setSaveError(t('organization.guestFailed'))
    } finally {
      setSavingUserId(null)
    }
  }

  return (
    <SettingsSection title={t('organization.membersTitle')}>
      {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      {/* Fills the pane; scrolls sideways inside its frame on narrow panes. */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[600px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                {t('organization.user')}
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                <span className="flex items-center gap-1">
                  {t('organization.role')}
                  <OrgRoleInfoPopover />
                </span>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                {t('organization.guest')}
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                {t('organization.joined')}
              </th>
              {isOwner && (
                <th className="text-left px-4 py-3 font-medium text-muted-foreground"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const isLastOwner = m.role === 'owner' && ownerCount < 2
              const isSaving = savingUserId === m.userId
              const canEdit = isOwner

              return (
                <tr
                  key={m.userId}
                  className={`border-b border-border last:border-b-0${m.disabledAt ? ' opacity-50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {m.avatarUrl ? (
                        <img
                          src={toApiUrl(m.avatarUrl)}
                          alt=""
                          className="h-7 w-7 rounded-full object-cover border border-border"
                        />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-muted border border-border flex items-center justify-center text-[10px] text-muted-foreground font-medium">
                          {(m.name ?? m.email).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-foreground">{m.name ?? m.email}</p>
                        {m.name && <p className="text-xs text-muted-foreground">{m.email}</p>}
                      </div>
                      {m.disabledAt && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                          {t('organization.disabled')}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {canEdit ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <select
                                className="h-8 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                value={m.role}
                                onChange={(e) => void handleRoleChange(m.userId, e.target.value)}
                                disabled={isSaving || isLastOwner}
                              >
                                <option value="owner">{t('role.owner')}</option>
                                <option value="admin">{t('role.admin')}</option>
                                <option value="member">{t('role.member')}</option>
                              </select>
                            </div>
                          </TooltipTrigger>
                          {isLastOwner && (
                            <TooltipContent>
                              <p>{t('organization.lastOwnerTooltip')}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-sm text-foreground">{roleLabel(m.role)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {canEdit ? (
                      <GuestCheckbox
                        checked={m.isGuest}
                        onCheckedChange={(checked) => void handleGuestChange(m.userId, checked)}
                        disabled={isSaving}
                        lockedReason={
                          isOrgAdminRole(m.role) ? t('organization.guestAdminTooltip') : null
                        }
                      />
                    ) : m.isGuest ? (
                      <span className="text-sm text-muted-foreground">{t('organization.yes')}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">{t('organization.no')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                    {new Date(m.joinedAt).toLocaleDateString(intl)}
                  </td>
                  {isOwner && (
                    <td className="px-4 py-3">
                      {m.role !== 'owner' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          disabled={isSaving}
                          onClick={() => void handleToggleDisabled(m.userId, !m.disabledAt)}
                        >
                          {m.disabledAt ? t('organization.enable') : t('organization.disable')}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{t('organization.membersHint')}</p>
    </SettingsSection>
  )
}
